import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const DESKTOP_BREAKPOINT_PX = 768;
const MIN_RIGHT_SHEET_WIDTH_REM = 24;
const MAX_RIGHT_SHEET_WIDTH_VIEWPORT_RATIO = 0.9;
const RIGHT_SHEET_WIDTH_STORAGE_KEY = "admin-portal:right-sheet-width-rem";
const KEYBOARD_RESIZE_STEP_REM = 2;

function getRootFontSize() {
  if (typeof window === "undefined") {
    return 16;
  }

  const rootFontSize = Number.parseFloat(
    window.getComputedStyle(document.documentElement).fontSize,
  );

  return Number.isFinite(rootFontSize) && rootFontSize > 0 ? rootFontSize : 16;
}

function clampRightSheetWidthRem(widthRem: number) {
  if (typeof window === "undefined") {
    return Math.max(widthRem, MIN_RIGHT_SHEET_WIDTH_REM);
  }

  const maxWidthRem =
    (window.innerWidth * MAX_RIGHT_SHEET_WIDTH_VIEWPORT_RATIO) / getRootFontSize();

  return Math.min(
    Math.max(widthRem, MIN_RIGHT_SHEET_WIDTH_REM),
    Math.max(MIN_RIGHT_SHEET_WIDTH_REM, maxWidthRem),
  );
}

function readStoredRightSheetWidthRem() {
  if (typeof window === "undefined") {
    return null;
  }

  const storedWidth = window.localStorage.getItem(RIGHT_SHEET_WIDTH_STORAGE_KEY);
  const parsedWidth = Number.parseFloat(storedWidth ?? "");

  if (!Number.isFinite(parsedWidth)) {
    return null;
  }

  return clampRightSheetWidthRem(parsedWidth);
}

function writeStoredRightSheetWidthRem(widthRem: number) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    RIGHT_SHEET_WIDTH_STORAGE_KEY,
    clampRightSheetWidthRem(widthRem).toString(),
  );
}

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  style,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
}) {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const dragStateRef = React.useRef<{
    pointerId: number;
    startX: number;
    startWidthRem: number;
    previousUserSelect: string;
    previousCursor: string;
  } | null>(null);
  const [isDesktopViewport, setIsDesktopViewport] = React.useState(false);
  const [widthRem, setWidthRem] = React.useState<number | null>(null);
  const isResizableRightSheet = side === "right" && isDesktopViewport;

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleViewportChange = () => {
      const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT_PX;
      setIsDesktopViewport(isDesktop);
      setWidthRem((currentWidthRem) => {
        if (!isDesktop || currentWidthRem === null) {
          return currentWidthRem;
        }

        return clampRightSheetWidthRem(currentWidthRem);
      });
    };

    handleViewportChange();
    window.addEventListener("resize", handleViewportChange);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
    };
  }, []);

  React.useEffect(() => {
    if (!isResizableRightSheet) {
      return undefined;
    }

    const storedWidthRem = readStoredRightSheetWidthRem();
    if (storedWidthRem !== null) {
      setWidthRem(storedWidthRem);
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const content = contentRef.current;
      if (!content) {
        return;
      }

      setWidthRem(
        clampRightSheetWidthRem(content.getBoundingClientRect().width / getRootFontSize()),
      );
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isResizableRightSheet]);

  React.useEffect(() => {
    if (!isResizableRightSheet || widthRem === null) {
      return;
    }

    writeStoredRightSheetWidthRem(widthRem);
  }, [isResizableRightSheet, widthRem]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const stopResizing = (pointerId?: number) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      if (pointerId !== undefined && dragState.pointerId !== pointerId) {
        return;
      }

      document.body.style.userSelect = dragState.previousUserSelect;
      document.body.style.cursor = dragState.previousCursor;
      dragStateRef.current = null;
    };

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      const deltaRem = (dragState.startX - event.clientX) / getRootFontSize();
      setWidthRem(clampRightSheetWidthRem(dragState.startWidthRem + deltaRem));
    };

    const handlePointerUp = (event: PointerEvent) => {
      stopResizing(event.pointerId);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      stopResizing();
    };
  }, []);

  const getMeasuredWidthRem = React.useCallback(() => {
    const content = contentRef.current;

    if (!content) {
      return MIN_RIGHT_SHEET_WIDTH_REM;
    }

    return clampRightSheetWidthRem(content.getBoundingClientRect().width / getRootFontSize());
  }, []);

  const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizableRightSheet) {
      return;
    }

    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    event.preventDefault();

    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidthRem: widthRem ?? getMeasuredWidthRem(),
      previousUserSelect: document.body.style.userSelect,
      previousCursor: document.body.style.cursor,
    };

    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isResizableRightSheet) {
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setWidthRem((currentWidthRem) =>
        clampRightSheetWidthRem(
          (currentWidthRem ?? getMeasuredWidthRem()) + KEYBOARD_RESIZE_STEP_REM,
        ),
      );
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      setWidthRem((currentWidthRem) =>
        clampRightSheetWidthRem(
          (currentWidthRem ?? getMeasuredWidthRem()) - KEYBOARD_RESIZE_STEP_REM,
        ),
      );
    }
  };

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={contentRef}
        data-slot="sheet-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition-[opacity,transform] ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          side === "right" &&
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
          side === "left" &&
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" &&
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
          side === "bottom" &&
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
          className,
        )}
        style={
          isResizableRightSheet && widthRem !== null
            ? {
                ...style,
                width: `${widthRem}rem`,
                maxWidth: `${widthRem}rem`,
              }
            : style
        }
        {...props}
      >
        {isResizableRightSheet ? (
          <div
            role="separator"
            aria-label="Resize panel"
            aria-orientation="vertical"
            tabIndex={0}
            className="group absolute inset-y-0 left-0 z-10 flex w-4 -translate-x-1/2 cursor-col-resize touch-none select-none items-center justify-center outline-none"
            onPointerDown={handleResizeStart}
            onKeyDown={handleResizeKeyDown}
          >
            <div className="bg-border h-16 w-0.5 rounded-full transition-colors group-hover:bg-foreground/40 group-focus-visible:bg-foreground/40" />
          </div>
        ) : null}
        {children}
        <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
};
